
"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle, AlertDialogAction, AlertDialogCancel, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Candidate } from "./candidates/candidate-types";
import { RoleTable } from "./candidates/RoleTable";
import { CandidateTable } from "./candidates/CandidateTable";
import { SearchOptionsPanel } from "./candidates/SearchOptionsPanel";
import { ViewJobDescriptionDialog } from "./ViewJobDescriptionDialog";
import { InternalCandidates } from "./InternalCandidates";
import { useCandidateSearch } from "./candidates/hooks/useCandidateSearch";
import { useClearCandidates } from "./candidates/hooks/useClearCandidates";
import { useProjectData } from "./candidates/hooks/useProjectData";
import { useDataIntegrity } from "./candidates/hooks/useDataIntegrity";

export function FindCandidates() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [useRoleTitle, setUseRoleTitle] = useState(true);
  const [useRoleLocation, setUseRoleLocation] = useState(true);
  const [useOpenToWork, setUseOpenToWork] = useState(false);
  const [aiEngine, setAiEngine] = useState("openai");
  const [showProjectIdDialog, setShowProjectIdDialog] = useState(false);
  const [dialogProjectId, setDialogProjectId] = useState<string | null>(null);
  const [hasValidJobDescriptions, setHasValidJobDescriptions] = useState(false);
  const [showViewJDDialog, setShowViewJDDialog] = useState(false);
  const [selectedRoleForJD, setSelectedRoleForJD] = useState<string | null>(null);

  const {
    projectId,
    projectName,
    roles,
    rolesLoading,
    candidatesPerRole,
    setCandidatesPerRole
  } = useProjectData();

  const {
    processLog,
    setProcessLog,
    analyzing,
    searching,
    currentProcessingId,
    isGeneratingAll,
    currentRoleIndex,
    textAreaRef,
    handleRowSelect: handleRowSelectBase,
    handleGenerateAll: handleGenerateAllBase
  } = useCandidateSearch();

  const {
    isClearing,
    showClearConfirmDialog,
    setShowClearConfirmDialog,
    handleClearCandidatesConfirm,
    handleClearCandidates
  } = useClearCandidates();

  // Initialize data integrity checking
  useDataIntegrity(projectId, candidatesPerRole);

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.scrollTop = textAreaRef.current.scrollHeight;
    }
  }, [processLog]);

  useEffect(() => {
    const checkAndTriggerSearch = async () => {
      if (!projectId) return;

      try {
        const { data: candidates, error: candidatesError } = await supabase
          .from('candidates')
          .select('candidate_name, role_title')
          .eq('project_id', projectId)
          .is('candidate_name', null);

        if (candidatesError) {
          console.error('Error fetching candidates:', candidatesError);
          return;
        }

        if (candidates && candidates.length > 0) {
          const roleTitlesToProcess = [...new Set(candidates.map(c => c.role_title))];
          
          for (const roleTitle of roleTitlesToProcess) {
            const { data: roleData, error: roleError } = await supabase
              .from('role_jd')
              .select('*')
              .eq('project_id', projectId)
              .eq('role_title', roleTitle)
              .single();

            if (roleError) {
              console.error(`Error fetching role data for ${roleTitle}:`, roleError);
              continue;
            }

            if (roleData) {
              await handleRowSelect(roleData.id);
            }
          }
        }
      } catch (error) {
        console.error('Error in checkAndTriggerSearch:', error);
        toast.error("Failed to process candidates");
      }
    };

    checkAndTriggerSearch();
  }, [projectId]);

  const handleViewJD = (roleId: string) => {
    setSelectedRoleForJD(roleId);
    setShowViewJDDialog(true);
  };

  const handleRowSelect = async (rowId: string) => {
    await handleRowSelectBase(
      rowId,
      roles,
      selectedRowId,
      useRoleTitle,
      useRoleLocation,
      useOpenToWork,
      aiEngine,
      setCandidates,
      setCandidatesPerRole,
      setSelectedRowId
    );
  };

  const handleGenerateAll = async () => {
    await handleGenerateAllBase(roles, candidatesPerRole, handleRowSelect);
  };

  const handleClearCandidatesConfirmWrapper = async () => {
    await handleClearCandidatesConfirm(
      projectId,
      setCandidates,
      setCandidatesPerRole,
      setSelectedRowId,
      setProcessLog
    );
  };

  const handleBack = () => {
    if (!projectId) return;
    navigate(`/dashboard?step=3&projectId=${projectId}`);
  };

  const handleNext = async () => {
    if (!projectId) {
      toast.error("Project ID is missing");
      return;
    }

    try {
      navigate(`/dashboard?step=5&projectId=${projectId}`);
      toast.success("Moving to Summary");
    } catch (error) {
      console.error('Error in handleNext:', error);
      toast.error("Failed to navigate to next step");
    }
  };

  return (
    <div className="h-[520px]">
      <div className="space-y-2 bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-4 py-[17px] my-[21px]">
        

        {/* Internal Candidates Section */}
        <div className="mt-4 space-y-2">
          <Collapsible defaultOpen={false}>
              <CollapsibleTrigger className="flex items-center justify-center w-full rounded-md border border-primary/20 p-2 hover:bg-primary/10 py-[8px] my-px">
                <h3 className="text-[14px] font-medium text-primary-light text-center flex items-center gap-2">
                  Existing Candidate Pool (click to expand or collapse view)
                  <ChevronDown size={16} className="text-primary-light" />
                </h3>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <InternalCandidates />
              </CollapsibleContent>
          </Collapsible>
        </div>

        
        {projectName && (
          <h2 className="text-[10px] font-semibold text-white">
            Project: {projectName} 
            <span className="text-[10px] font-normal text-primary-light ml-2">
              (ProjectID: {projectId})
            </span>
          </h2>
        )}

        <AlertDialog open={showProjectIdDialog} onOpenChange={setShowProjectIdDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Project ID Information</AlertDialogTitle>
              <AlertDialogDescription>
                The current Project ID displayed on top of the Candidates table is: {dialogProjectId}
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={showClearConfirmDialog} onOpenChange={setShowClearConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear All Candidates</AlertDialogTitle>
              <AlertDialogDescription className="text-black">
                Are you sure you want to clear all candidate data for this project? This action cannot be undone and will help resolve any data inconsistency issues.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white text-black" onClick={() => setShowClearConfirmDialog(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction className="bg-primary text-black" onClick={handleClearCandidatesConfirmWrapper}>
                Clear Candidates
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ViewJobDescriptionDialog
          open={showViewJDDialog}
          onOpenChange={setShowViewJDDialog}
          roleId={selectedRoleForJD || ''}
        />

        <div className="mt-2 space-y-2 py-0 my-[10px]">
          <SearchOptionsPanel 
            useRoleTitle={useRoleTitle}
            setUseRoleTitle={setUseRoleTitle}
            useRoleLocation={useRoleLocation}
            setUseRoleLocation={setUseRoleLocation}
            useOpenToWork={useOpenToWork}
            setUseOpenToWork={setUseOpenToWork}
            aiEngine={aiEngine}
            setAiEngine={setAiEngine}
            processLog={processLog}
            handleGenerateAll={handleGenerateAll}
            isGeneratingAll={isGeneratingAll}
            searching={searching}
            textAreaRef={textAreaRef}
            handleClearCandidates={handleClearCandidates}
            isClearing={isClearing}
          />
          
          <RoleTable 
            roles={roles}
            candidatesPerRole={candidatesPerRole}
            selectedRowId={selectedRowId}
            onRowSelect={handleRowSelect}
            loading={loading}
            currentProcessingId={currentProcessingId}
            searching={searching}
            rolesLoading={rolesLoading}
            onViewJD={handleViewJD}
          />

          

          <div className="mt-2 space-y-2">
            <Collapsible defaultOpen={false}>
              <CollapsibleTrigger className="flex items-center justify-center w-full rounded-md border border-primary/20 p-2 hover:bg-primary/10 py-[8px] my-px">
                <h3 className="text-[14px] font-medium text-primary-light text-center flex items-center gap-2">
                  Matched Candidates (click to expand or collapse view)
                  <ChevronDown size={16} className="text-primary-light" />
                </h3>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <CandidateTable candidates={candidates} />
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-4 my-[14px]">
        <Button onClick={handleBack} className="w-32 bg-white text-secondary hover:bg-white/90">
          Back
        </Button>
        <Button onClick={handleNext} className="w-32">
          Next
        </Button>
      </div>
    </div>
  );
}

export default FindCandidates;
